// Detects links inside a rep's plain-text chat message so the UI can render them as real
// clickable anchors instead of dead text (see ChatDock.tsx's MessageText).
//
// Two shapes are recognized, since reps write both:
//   1. Markdown links, e.g. "[the report](https://example.com/r.html)" -> shown as "the report"
//   2. Bare URLs, e.g. "https://example.com/r.html" or "www.example.com" -> shown as-is
//
// Nothing else about the message is treated as markdown; this is deliberately link-only.

export interface LinkMatch {
  // Offsets in the ORIGINAL string, so a caller can splice text segments around the match.
  start: number;
  end: number;
  // Where the anchor points (always absolute, "www." forms get an https:// scheme added).
  href: string;
  // What the anchor shows.
  label: string;
}

// "[label](url)" — the url may optionally be wrapped in angle brackets, as markdown allows.
const MARKDOWN_LINK_RE = /\[([^\]\n]{1,300})\]\(\s*<?((?:https?:\/\/|www\.)[^\s<>)]+)>?\s*\)/g;

// A bare http(s):// or www. URL, stopped by whitespace or characters that never appear in one.
const BARE_URL_RE = /(?:https?:\/\/|www\.)[^\s<>"'`\\]+/g;

// Sentence punctuation that trails a URL far more often than it belongs to it.
const TRAILING_PUNCT = new Set(['.', ',', ';', ':', '!', '?', '"', "'", '’', '”']);

function count(text: string, ch: string): number {
  let n = 0;
  for (const c of text) if (c === ch) n++;
  return n;
}

// Trims characters that are almost certainly prose, not part of the URL: sentence punctuation,
// and closing brackets that were never opened inside the URL itself (e.g. "(see https://x.com/a)").
function trimTrailingPunctuation(url: string): string {
  let out = url;
  while (out.length > 0) {
    const last = out[out.length - 1];
    if (TRAILING_PUNCT.has(last)) {
      out = out.slice(0, -1);
      continue;
    }
    if (last === ')' && count(out, ')') > count(out, '(')) {
      out = out.slice(0, -1);
      continue;
    }
    if (last === ']' && count(out, ']') > count(out, '[')) {
      out = out.slice(0, -1);
      continue;
    }
    break;
  }
  return out;
}

// Adds a scheme to "www.example.com" style URLs so the anchor isn't treated as a relative path.
function toHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// Returns every link in `text`, ordered by position and non-overlapping. Markdown links win over
// the bare-URL scan, so the url inside "[label](url)" isn't also matched on its own.
export function detectLinks(text: string): LinkMatch[] {
  if (!text) return [];
  const matches: LinkMatch[] = [];

  MARKDOWN_LINK_RE.lastIndex = 0;
  let md: RegExpExecArray | null;
  while ((md = MARKDOWN_LINK_RE.exec(text)) !== null) {
    const url = trimTrailingPunctuation(md[2]);
    if (!url) continue;
    matches.push({
      start: md.index,
      end: md.index + md[0].length,
      href: toHref(url),
      label: md[1].trim() || url,
    });
  }

  BARE_URL_RE.lastIndex = 0;
  let bare: RegExpExecArray | null;
  while ((bare = BARE_URL_RE.exec(text)) !== null) {
    const url = trimTrailingPunctuation(bare[0]);
    // A bare "www." run needs at least one dot after it to look like a host.
    if (!url || (!/^https?:\/\//i.test(url) && !/^www\.[^.]+\./i.test(url))) continue;
    const start = bare.index;
    const end = start + url.length;
    // Skip anything already covered by a markdown link.
    if (matches.some((m) => start < m.end && end > m.start)) continue;
    matches.push({ start, end, href: toHref(url), label: url });
  }

  return matches.sort((a, b) => a.start - b.start);
}
